import React, { useEffect } from 'react';
import './TermsPage.css';

const TermsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="terms-page">
      <div className="terms-container">
        <div className="terms-header">
          <div className="terms-header-spacer" />
          <h1 className="terms-title">Terms of Use</h1>
          <p className="terms-effective-date">Effective Date: November 13, 2025</p>
        </div>

        <div className="terms-content">
          <section className="terms-section">
            <h2 className="section-title">1. Agreement to Terms</h2>
            <p>
              These Terms of Use ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") 
              and Playlounge LLC ("Company", "we", "us", or "our"), regarding your 
              access to and use of the Novembr platform, services, and website (collectively, the "Service"). 
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, 
              you may not access or use the Service.
            </p>
            <p>
              You must be at least 18 years old to use the Service. By using the Service, you represent and warrant that 
              you are at least 18 years of age and have the legal capacity to enter into these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">2. Description of Service</h2>
            <p>
              The Service provides application programming interface (API) services that enable you to analyze user profiles, 
              images, and text content for trustworthiness and authenticity. The Service includes, but is not limited to:
            </p>
            <ul>
              <li>Trust Score API - Comprehensive analysis combining all detection methods to generate an overall trustworthiness score</li>
              <li>Face Detection API - Detect faces in images with detailed analysis including face count, confidence scores, and bounding box coordinates</li>
              <li>Batch Face Detection API - Process multiple images simultaneously for face detection</li>
              <li>AI Image Detection API - Identify AI-generated or synthetic images using advanced machine learning models</li>
              <li>Composite Check API - Combine face detection and AI image detection into a single request</li>
              <li>Text Analysis API - Analyze bio text, prompts, and metadata to detect authenticity and identify suspicious patterns</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">3. User Accounts and Registration</h2>
            <p>
              To access certain features of the Service, you must register for an account. When you register, you agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p>
              You are responsible for maintaining the confidentiality of your API keys and account credentials. You agree not 
              to share your API keys with any third party. You are solely responsible for all activities that occur under your 
              account, whether authorized by you or not.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">4. API Usage and Restrictions</h2>
            <p>
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, 
              revocable license to access and use the Service for your internal business purposes.
            </p>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose or in violation of any applicable laws or regulations</li>
              <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service</li>
              <li>Modify, adapt, alter, or create derivative works of the Service</li>
              <li>Use the Service to violate any third party's intellectual property rights or privacy rights</li>
              <li>Use the Service to transmit any viruses, malware, or other harmful code</li>
              <li>Attempt to gain unauthorized access to the Service or any related systems or networks</li>
              <li>Use automated systems (bots, scrapers, etc.) to access the Service in a manner that exceeds reasonable request rates</li>
              <li>Resell, sublicense, or otherwise distribute access to the Service without our prior written consent</li>
              <li>Use the Service in any manner that could damage, disable, overburden, or impair our servers or networks</li>
              <li>Use the Service to build a competing product or service</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="section-title">5. Pricing and Payment</h2>
            <p>
              The Service operates on a usage-based pricing model. We offer a free tier that includes a limited number of 
              requests per month. Additional usage beyond the free tier is charged according to our published pricing, which 
              may be updated from time to time.
            </p>
            <p>
              By using the Service, you agree to pay all fees associated with your usage. All fees are calculated based on 
              actual API usage and are billed monthly. You must provide a valid payment method to use the Service beyond the 
              free tier. We use Stripe, Inc. ("Stripe") as our payment processor, and your use of Stripe's services is subject 
              to Stripe's terms and conditions.
            </p>
            <p>
              You authorize us to charge your payment method for all fees incurred. If your payment method fails or is declined, 
              we may suspend or terminate your access to the Service. All fees are non-refundable unless otherwise required by law.
            </p>
            <p>
              We reserve the right to change our pricing at any time. We will provide reasonable notice of any material pricing 
              changes. Continued use of the Service after a pricing change constitutes your acceptance of the new pricing.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">6. Intellectual Property Rights</h2>
            <p>
              The Service, including all content, features, functionality, and technology, is owned by Playlounge LLC and is 
              protected by United States and international copyright, trademark, patent, trade secret, and other intellectual 
              property laws.
            </p>
            <p>
              These Terms do not grant you any right, title, or interest in or to the Service, except for the limited license 
              to use the Service as set forth herein. All trademarks, service marks, logos, and trade names displayed on the 
              Service are the property of their respective owners.
            </p>
            <p>
              You retain ownership of any data, content, or materials you submit to the Service ("User Content"). By submitting 
              User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, process, and store your User 
              Content solely for the purpose of providing the Service to you.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">7. Data and Privacy</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. 
              Please review our Privacy Policy to understand how we collect, use, and protect your information.
            </p>
            <p>
              You are responsible for ensuring that any data you submit to the Service complies with applicable data protection 
              laws and that you have obtained all necessary consents and authorizations from individuals whose data you submit.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">8. Service Availability and Modifications</h2>
            <p>
              We strive to provide reliable service, but we do not guarantee that the Service will be available at all times or 
              that it will be error-free. The Service may be unavailable due to maintenance, updates, technical issues, or other 
              reasons beyond our control.
            </p>
            <p>
              We reserve the right to modify, update, or discontinue any part of the Service at any time, with or without notice. 
              We may also impose limits on certain features or restrict your access to parts or all of the Service without notice 
              or liability.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">9. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
              NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
            <p>
              We do not warrant that the Service will be uninterrupted, secure, or error-free, or that defects will be corrected. 
              We do not warrant or make any representations regarding the accuracy, reliability, or availability of the Service or 
              the results obtained from using the Service.
            </p>
            <p>
              The Service uses machine learning and artificial intelligence technologies. While we strive for accuracy, we do not 
              guarantee that the results provided by the Service will be 100% accurate or error-free. You should not rely solely 
              on the Service for making critical decisions and should use your own judgment and additional verification methods.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Playlounge LLC, its affiliates, officers, directors, employees, 
              agents, and licensors from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, 
              and expenses (including but not limited to attorney's fees) arising from:
            </p>
            <ul>
              <li>Your use of and access to the Service</li>
              <li>Your violation of any term of these Terms</li>
              <li>Your violation of any third party right, including without limitation any copyright, property, or privacy right</li>
              <li>Any claim that your User Content caused damage to a third party</li>
              <li>Your violation of any applicable law or regulation</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2 className="section-title">11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, 
              for any reason, including, without limitation, if you breach these Terms.
            </p>
            <p>
              Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, 
              you may do so by contacting us at teamdecembr@gmail.com or through your account settings.
            </p>
            <p>
              All provisions of these Terms which by their nature should survive termination shall survive termination, including, 
              without limitation, ownership provisions, warranty disclaimers, and indemnity.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">12. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard 
              to its conflict of law provisions.
            </p>
            <p>
              Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding arbitration 
              in accordance with the Commercial Arbitration Rules of the American Arbitration Association. The arbitration shall 
              be conducted in Atlanta, Georgia. Judgment on the arbitral award may be entered in any court having jurisdiction thereof.
            </p>
            <p>
              Notwithstanding the foregoing, we may seek injunctive or other equitable relief in any court of competent jurisdiction 
              to protect our intellectual property rights or to prevent unauthorized use of the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the 
              new Terms on this page and updating the "Effective Date" at the top of these Terms.
            </p>
            <p>
              Your continued use of the Service after any such changes constitutes your acceptance of the new Terms. If you do 
              not agree to the modified Terms, you must stop using the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2 className="section-title">14. Miscellaneous</h2>
            <p>
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement 
              between you and Playlounge LLC regarding the Service and supersede all prior agreements and understandings.
            </p>
            <p>
              <strong>Severability:</strong> If any provision of these Terms is held to be invalid or unenforceable, the remaining 
              provisions will remain in full force and effect.
            </p>
            <p>
              <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be considered a waiver 
              of such right or provision.
            </p>
            <p>
              <strong>Assignment:</strong> You may not assign or transfer these Terms or your rights hereunder, in whole or in part, 
              without our prior written consent. We may assign these Terms at any time without notice.
            </p>
            <p>
              <strong>Contact Information:</strong> If you have any questions about these Terms, please contact us at:
            </p>
            <div className="contact-info">
              <p>Novembr</p>
              <p>Email: contact@novembr.dev</p>
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

export default TermsPage;

